import React, { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, MapPin, Map, Lightbulb, RefreshCw } from "lucide-react";
import "./LocationShare.css";

const LocationShare = ({ 
  value, 
  onChange, 
  placeholder = "Adresse complète",
  label = "Adresse",
  required = false,
  name = "address"
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Initialiser la carte
  const initializeMap = useCallback(() => {
    if (!window.L || !mapContainerRef.current || !coordinates) {
      return;
    }

    // Vérifier si le conteneur est déjà utilisé
    if (mapContainerRef.current._leaflet_id) {
      // Le conteneur est déjà utilisé, ne pas recréer
      return;
    }

    // Détruire la carte existante si elle existe
    if (mapRef.current) {
      try {
        mapRef.current.remove();
      } catch (e) {
        // Ignorer l'erreur si la carte est déjà supprimée
      }
      mapRef.current = null;
      markerRef.current = null;
    }

    // Vider le conteneur pour éviter les conflits
    mapContainerRef.current.innerHTML = '';

    // S'assurer que le conteneur a une taille et les bons styles
    mapContainerRef.current.style.width = '100%';
    mapContainerRef.current.style.height = '400px';
    mapContainerRef.current.style.position = 'relative';
    mapContainerRef.current.style.overflow = 'hidden';
    mapContainerRef.current.style.boxSizing = 'border-box';

    try {
      // Créer la carte
      const map = window.L.map(mapContainerRef.current, {
        center: [coordinates.lat, coordinates.lng],
        zoom: 15,
        zoomControl: true,
        preferCanvas: false
      });
      mapRef.current = map;

      // Attendre que la carte soit complètement initialisée avant d'ajouter les tuiles
      setTimeout(() => {
        if (!mapRef.current) return;

        // Ajouter les tuiles OpenStreetMap
        const tileLayer = window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
          tileSize: 256,
          zoomOffset: 0
        });
        tileLayer.addTo(map);

        // Forcer le redimensionnement immédiatement
        map.invalidateSize();

        // Créer une icône SVG personnalisée comme fallback
        const createCustomIcon = () => {
          const svgIcon = window.L.divIcon({
            className: 'custom-marker-icon',
            html: `
              <div style="
                width: 30px;
                height: 30px;
                background-color: #3388ff;
                border: 3px solid white;
                border-radius: 50% 50% 50% 0;
                transform: rotate(-45deg);
                box-shadow: 0 2px 5px rgba(0,0,0,0.3);
                position: relative;
              ">
                <div style="
                  width: 12px;
                  height: 12px;
                  background-color: white;
                  border-radius: 50%;
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%) rotate(45deg);
                "></div>
              </div>
            `,
            iconSize: [30, 30],
            iconAnchor: [15, 30],
            popupAnchor: [0, -30]
          });
          return svgIcon;
        };

        // Essayer d'abord avec l'icône Leaflet standard
        let defaultIcon;
        try {
          defaultIcon = window.L.icon({
            iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
            iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
            shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
            shadowAnchor: [12, 41]
          });
        } catch (e) {
          console.warn("Erreur lors de la création de l'icône Leaflet, utilisation de l'icône SVG:", e);
          defaultIcon = createCustomIcon();
        }
        
        // Définir l'icône par défaut pour tous les marqueurs
        window.L.Marker.prototype.options.icon = defaultIcon;

        // Ajouter un marqueur avec l'icône explicitement définie
        const marker = window.L.marker([coordinates.lat, coordinates.lng], {
          draggable: true,
          icon: defaultIcon,
          zIndexOffset: 1000,
          riseOnHover: true
        });
        
        marker.addTo(map);
        markerRef.current = marker;
        
        // Vérifier que le marqueur est bien ajouté et visible
        setTimeout(() => {
          if (markerRef.current) {
            const markerElement = markerRef.current.getElement();
            if (markerElement) {
              markerElement.style.zIndex = '1000';
              markerElement.style.display = 'block';
              markerElement.style.visibility = 'visible';
              markerElement.style.opacity = '1';
            }
          }
        }, 100);

        // Forcer le redimensionnement après l'ajout du marqueur
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.invalidateSize();
          }
        }, 200);

        // Gérer le clic sur la carte
        map.on('click', async (e) => {
          const { lat, lng } = e.latlng;
          await updateLocationFromCoordinates(lat, lng);
          if (markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
          }
        });

        // Gérer le glissement du marqueur
        marker.on('dragend', async (e) => {
          const { lat, lng } = e.target.getLatLng();
          await updateLocationFromCoordinates(lat, lng);
        });
      }, 150);
    } catch (error) {
      console.error("Erreur lors de l'initialisation de la carte:", error);
      setError("Erreur lors du chargement de la carte");
    }
  }, [coordinates]);

  // Fonction pour géocoder les coordonnées et mettre à jour l'adresse
  const updateLocationFromCoordinates = async (lat, lng) => {
    setLoading(true);
    setError("");
    
    // Mettre à jour les coordonnées mais ne pas recréer la carte
    const newCoords = { lat, lng };
    setCoordinates(newCoords);

    try {
      // Utiliser l'API de géocodage inverse de Nominatim (OpenStreetMap)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'PouletDirect/1.0'
          }
        }
      );
      
      const data = await response.json();
      
      if (data && data.address) {
        // Construire l'adresse complète
        const addressParts = [];
        if (data.address.road) addressParts.push(data.address.road);
        if (data.address.house_number) addressParts.push(data.address.house_number);
        if (data.address.quarter) addressParts.push(data.address.quarter);
        if (data.address.suburb) addressParts.push(data.address.suburb);
        if (data.address.city || data.address.town || data.address.village) {
          addressParts.push(data.address.city || data.address.town || data.address.village);
        }
        
        const fullAddress = addressParts.length > 0 
          ? addressParts.join(", ")
          : data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        
        // Ajouter les coordonnées à la fin
        const addressWithCoords = `${fullAddress} (${lat.toFixed(6)}, ${lng.toFixed(6)})`;
        
        if (onChange) {
          onChange({
            target: {
              name: name,
              value: addressWithCoords
            }
          });
        }
      } else {
        // Si pas d'adresse trouvée, utiliser juste les coordonnées
        const addressWithCoords = `Position: ${lat.toFixed(6)}, ${lng.toFixed(6)} (${lat.toFixed(6)}, ${lng.toFixed(6)})`;
        if (onChange) {
          onChange({
            target: {
              name: name,
              value: addressWithCoords
            }
          });
        }
      }
    } catch (err) {
      console.error("Erreur de géocodage:", err);
      // En cas d'erreur, utiliser juste les coordonnées
      const addressWithCoords = `Position: ${lat.toFixed(6)}, ${lng.toFixed(6)} (${lat.toFixed(6)}, ${lng.toFixed(6)})`;
      if (onChange) {
        onChange({
          target: {
            name: name,
            value: addressWithCoords
          }
        });
      }
      setError("Impossible de retrouver l'adresse à partir de la position. Votre position GPS a bien été enregistrée, mais vous pouvez aussi corriger le texte de l'adresse manuellement.");
    }
    
    setLoading(false);
  };

  // Charger Leaflet dynamiquement
  useEffect(() => {
    if (!showMap || !coordinates) {
      // Nettoyage si la carte est masquée
      if (mapRef.current) {
        try {
          mapRef.current.remove();
          mapRef.current = null;
          markerRef.current = null;
        } catch (e) {
          // Ignorer les erreurs de nettoyage
        }
      }
      return;
    }

    // Si la carte existe déjà, ne pas la recréer
    if (mapRef.current && markerRef.current) {
      // Mettre à jour juste la position du marqueur si nécessaire
      const currentLatLng = markerRef.current.getLatLng();
      if (Math.abs(currentLatLng.lat - coordinates.lat) > 0.0001 || 
          Math.abs(currentLatLng.lng - coordinates.lng) > 0.0001) {
        markerRef.current.setLatLng([coordinates.lat, coordinates.lng]);
        mapRef.current.setView([coordinates.lat, coordinates.lng], mapRef.current.getZoom());
      }
      return;
    }

    // Vérifier si Leaflet est déjà chargé
    if (window.L && mapLoaded) {
      // Leaflet est chargé, initialiser la carte
      const timer = setTimeout(() => {
        if (mapContainerRef.current && !mapRef.current && coordinates) {
          initializeMap();
        }
      }, 200);
      return () => clearTimeout(timer);
    }

    // Charger les CSS de Leaflet
    const existingLink = document.querySelector('link[href*="leaflet.css"]');
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoSR5mXmwtcRAxs=';
      link.crossOrigin = '';
      document.head.appendChild(link);
    }

    // Charger le script Leaflet
    const existingScript = document.querySelector('script[src*="leaflet.js"]');
    if (!existingScript && !window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      script.crossOrigin = '';
      script.onload = () => {
        setMapLoaded(true);
        // Attendre que le DOM soit prêt et que les styles soient chargés
        setTimeout(() => {
          if (mapContainerRef.current && coordinates && !mapRef.current) {
            initializeMap();
          }
        }, 300);
      };
      script.onerror = () => {
        setError("Erreur lors du chargement de la bibliothèque de cartes");
      };
      document.body.appendChild(script);
    } else if (window.L) {
      // Leaflet déjà chargé
      setMapLoaded(true);
      setTimeout(() => {
        if (mapContainerRef.current && coordinates && !mapRef.current) {
          initializeMap();
        }
      }, 200);
    }

    // Nettoyage lors du démontage
    return () => {
      // Ne pas nettoyer si on change juste les coordonnées
      if (!showMap && mapRef.current) {
        try {
          mapRef.current.remove();
          mapRef.current = null;
          markerRef.current = null;
        } catch (e) {
          // Ignorer les erreurs
        }
      }
    };
  }, [showMap, coordinates, mapLoaded, initializeMap]);

  // Fonction pour obtenir la position actuelle et géocoder l'adresse
  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setError("La géolocalisation n'est pas supportée par votre navigateur");
      return;
    }

    setLoading(true);
    setError("");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        await updateLocationFromCoordinates(latitude, longitude);
        setShowMap(true);
      },
      (err) => {
        setError(`Erreur de géolocalisation: ${err.message}`);
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Extraire les coordonnées de l'adresse si elles existent
  const extractCoordinates = (addr) => {
    if (!addr) return null;
    const match = addr.match(/\((-?\d+\.?\d*),\s*(-?\d+\.?\d*)\)/);
    if (match) {
      return {
        lat: parseFloat(match[1]),
        lng: parseFloat(match[2])
      };
    }
    return null;
  };

  // Fonction pour réajouter les coordonnées si elles ont été supprimées
  const restoreCoordinates = () => {
    if (coordinates && value) {
      // Vérifier si les coordonnées sont déjà présentes
      if (!extractCoordinates(value)) {
        // Ajouter les coordonnées à la fin
        const addressWithCoords = `${value.trim()} (${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)})`;
        if (onChange) {
          onChange({
            target: {
              name: name,
              value: addressWithCoords
            }
          });
        }
      }
    }
  };

  // Extraire les coordonnées de l'adresse actuelle
  const existingCoords = extractCoordinates(value) || coordinates;
  const googleMapsLink = existingCoords 
    ? `https://www.google.com/maps?q=${existingCoords.lat},${existingCoords.lng}`
    : null;
  const openStreetMapLink = existingCoords
    ? `https://www.openstreetmap.org/?mlat=${existingCoords.lat}&mlon=${existingCoords.lng}&zoom=18`
    : null;

  // Mettre à jour les coordonnées quand la valeur change (mais ne pas recréer la carte)
  useEffect(() => {
    const coords = extractCoordinates(value);
    if (coords) {
      // Mettre à jour les coordonnées seulement si elles ont vraiment changé
      const hasChanged = !coordinates || 
        Math.abs(coords.lat - coordinates.lat) > 0.000001 || 
        Math.abs(coords.lng - coordinates.lng) > 0.000001;
      
      if (hasChanged) {
        // Mettre à jour les coordonnées
        setCoordinates(coords);
        
        // Mettre à jour le marqueur si la carte existe déjà
        if (mapRef.current && markerRef.current) {
          markerRef.current.setLatLng([coords.lat, coords.lng]);
          // Centrer la carte sur le nouveau point
          mapRef.current.setView([coords.lat, coords.lng], mapRef.current.getZoom());
        }
        
        if (!showMap && coords) {
          setShowMap(true);
        }
      }
    }
  }, [value, coordinates, showMap]);

  return (
    <div className="location-share-container">
      <div className="form-group">
        <label className="form-label">
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </label>
        <div className="address-input-group">
          <input
            type="text"
            className="form-control"
            value={value || ""}
            name={name}
            onChange={(e) => {
              if (onChange) {
                onChange(e);
              }
            }}
            placeholder={placeholder}
            required={required}
          />
          <button
            type="button"
            className="btn btn-outline btn-sm location-btn"
            onClick={getCurrentLocation}
            disabled={loading}
            title="Obtenir ma position actuelle"
          >
            {loading ? <Loader2 size={16} strokeWidth={2.25} className="spin" /> : <MapPin size={16} strokeWidth={2.25} />}
          </button>
          <button
            type="button"
            className="btn btn-outline btn-sm location-btn"
            onClick={() => {
              if (existingCoords) {
                setShowMap(!showMap);
              } else {
                // Si pas de coordonnées, obtenir la position d'abord
                getCurrentLocation();
              }
            }}
            title={showMap ? "Masquer la carte" : "Afficher la carte pour choisir un point"}
          >
            <Map size={16} strokeWidth={2.25} />
          </button>
        </div>
        {error && <div className="alert alert-error location-error">{error}</div>}
        {loading && (
          <div className="location-loading-indicator">
            <span className="spinner-small"></span>
            <span>Récupération de votre position...</span>
          </div>
        )}
        {existingCoords && (
          <div className="location-info">
            <p className="location-coords icon-inline">
              <MapPin size={13} strokeWidth={2.25} /> Coordonnées: {existingCoords.lat.toFixed(6)}, {existingCoords.lng.toFixed(6)}
            </p>
            <div className="location-map-buttons">
              <a
                href={googleMapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline btn-sm"
              >
                <Map size={14} strokeWidth={2.25} /> Voir sur Google Maps
              </a>
              <a
                href={openStreetMapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline btn-sm"
              >
                <Map size={14} strokeWidth={2.25} /> Voir sur OpenStreetMap
              </a>
            </div>
            {showMap && (
              <div className="location-map-preview">
                <div className="location-map-instructions icon-inline">
                  <Lightbulb size={15} strokeWidth={2.25} />
                  <p style={{margin: 0}}><strong>Cliquez sur la carte</strong> pour choisir un point, ou <strong>glissez le marqueur</strong> pour ajuster la position.</p>
                </div>
                <div 
                  ref={mapContainerRef} 
                  className="location-map-container"
                  style={{ height: '400px', width: '100%', borderRadius: 'var(--radius-sm)' }}
                ></div>
                <p className="location-map-note icon-inline">
                  <Lightbulb size={14} strokeWidth={2.25} /> <strong>Vous pouvez modifier l'adresse dans le champ ci-dessus.</strong> Si vous supprimez les coordonnées entre parenthèses, cliquez sur le bouton ci-dessous pour les restaurer.
                </p>
                {coordinates && !extractCoordinates(value) && (
                  <button
                    type="button"
                    onClick={restoreCoordinates}
                    className="btn btn-outline btn-sm"
                    style={{ marginTop: "0.5rem" }}
                  >
                    <RefreshCw size={14} strokeWidth={2.25} /> Restaurer les coordonnées GPS
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {coordinates && !extractCoordinates(value) && !showMap && (
          <div className="location-info" style={{ marginTop: "0.5rem" }}>
            <p className="location-map-note icon-inline" style={{ margin: 0 }}>
              <Lightbulb size={14} strokeWidth={2.25} /> Les coordonnées GPS ont été supprimées. Cliquez sur le bouton ci-dessous pour les restaurer.
            </p>
            <button
              type="button"
              onClick={restoreCoordinates}
              className="btn btn-outline btn-sm"
              style={{ marginTop: "0.5rem" }}
            >
              <RefreshCw size={14} strokeWidth={2.25} /> Restaurer les coordonnées GPS
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationShare;
